import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { BACKEND_URL } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/* Single dynamic auth card shown before checkout — email first, then
   expands in-place into Sign In or Create Account depending on whether
   the email belongs to an existing VinePass account. No page navigation;
   the booking/cart session already lives in CartContext + localStorage so
   nothing is lost while the guest authenticates. */
export const CheckoutAuthGate = ({ onAuthenticated }) => {
  const { login, register } = useAuth();
  const [phase, setPhase] = useState('email'); // 'email' | 'login' | 'register'
  const [visible, setVisible] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailInputRef = useRef(null);
  const nextFieldRef = useRef(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  // Fade-and-slide swap between phases — ~280ms total, no layout jump.
  const switchPhase = (next) => {
    setVisible(false);
    setTimeout(() => {
      setPhase(next);
      setVisible(true);
      setTimeout(() => nextFieldRef.current?.focus(), 60);
    }, 140);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Please enter your email address.');
      return;
    }
    setChecking(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/check-email`, { email: trimmed });
      switchPhase(res.data?.exists ? 'login' : 'register');
    } catch {
      toast.error('Could not verify your email. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);
    if (result.success) {
      onAuthenticated();
    } else {
      toast.error(result.error || 'Incorrect password. Please try again.');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const result = await register(fullName, email.trim(), password, 'customer');
    setSubmitting(false);
    if (result.success) {
      onAuthenticated();
    } else {
      toast.error(result.error || 'Could not create your account.');
    }
  };

  const bodyClass = `transition-all duration-200 ease-out ${
    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
  }`;

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center py-12 px-4">
      <div
        className="max-w-md w-full bg-white rounded-2xl p-6 sm:p-10 shadow-lg border border-[#E5E9E6]"
        data-testid="checkout-auth-gate"
      >
        <div className={bodyClass}>
          {phase === 'email' && (
            <>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-2 text-center">
                Continue to Checkout
              </p>
              <h2
                className="text-2xl font-semibold text-[#112318] text-center mb-3"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Let&apos;s get you booked in
              </h2>
              <p className="text-sm text-[#4A5D50] text-center mb-6">
                To complete your booking, please enter your email address.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="auth-email" className="text-[#4A5D50] text-sm">Email Address</Label>
                  <Input
                    id="auth-email"
                    ref={emailInputRef}
                    type="email"
                    autoComplete="email"
                    placeholder="jane@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="auth-gate-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={checking}
                  className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 text-lg font-medium transition-all shadow-md"
                  data-testid="auth-gate-continue-button"
                >
                  {checking ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>Continue <ArrowRight className="ml-2 w-5 h-5" /></>
                  )}
                </Button>
              </form>
              <p className="text-xs text-[#9AA89F] text-center mt-5 leading-relaxed">
                Your tickets, booking history and receipts will always be available in{' '}
                <span className="text-[#4A5D50] font-medium">My Tickets</span>, even if you&apos;re
                unable to access your confirmation email.
              </p>
            </>
          )}

          {phase === 'login' && (
            <>
              <h2
                className="text-2xl font-semibold text-[#112318] text-center mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Welcome Back
              </h2>
              <p className="text-sm text-[#4A5D50] text-center mb-4">
                Sign in to continue with your booking.
              </p>
              <div
                className="flex items-center justify-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5 w-fit mx-auto mb-5"
                data-testid="account-found-banner"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> We found your VinePass account.
              </div>
              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="auth-login-email" className="text-[#4A5D50] text-sm">Email Address</Label>
                  <Input
                    id="auth-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="auth-gate-login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="auth-login-password" className="text-[#4A5D50] text-sm">Password</Label>
                  <Input
                    id="auth-login-password"
                    ref={nextFieldRef}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="auth-gate-login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 text-lg font-medium transition-all shadow-md"
                  data-testid="auth-gate-signin-button"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                </Button>
                <Link
                  to="/forgot-password"
                  className="block text-center text-sm text-[#4A5D50] hover:text-[#1F3D2B]"
                  data-testid="auth-gate-forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </form>
            </>
          )}

          {phase === 'register' && (
            <>
              <h2
                className="text-2xl font-semibold text-[#112318] text-center mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Create Your Free VinePass Account
              </h2>
              <p className="text-sm text-[#4A5D50] text-center mb-4">
                You&apos;re almost there. Create your free VinePass account to complete your booking.
              </p>
              <div
                className="flex items-center justify-center gap-1.5 text-xs text-[#9C7434] bg-[#C9A27A]/10 border border-[#C9A27A]/30 rounded-full px-3 py-1.5 w-fit mx-auto mb-5"
                data-testid="new-guest-banner"
              >
                <Sparkles className="w-3.5 h-3.5" /> It looks like you&apos;re new to VinePass.
              </div>
              <form onSubmit={handleRegisterSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="auth-first-name" className="text-[#4A5D50] text-sm">First Name</Label>
                    <Input
                      id="auth-first-name"
                      ref={nextFieldRef}
                      type="text"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="mt-1"
                      data-testid="auth-gate-firstname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="auth-last-name" className="text-[#4A5D50] text-sm">Last Name</Label>
                    <Input
                      id="auth-last-name"
                      type="text"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="mt-1"
                      data-testid="auth-gate-lastname-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="auth-register-email" className="text-[#4A5D50] text-sm">Email Address</Label>
                  <Input
                    id="auth-register-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="auth-gate-register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="auth-register-password" className="text-[#4A5D50] text-sm">Password</Label>
                  <Input
                    id="auth-register-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="auth-gate-register-password-input"
                  />
                  <p className="text-[11px] text-[#9AA89F] mt-1">
                    Must be 8+ characters with uppercase, lowercase, and a number
                  </p>
                </div>
                <div>
                  <Label htmlFor="auth-confirm-password" className="text-[#4A5D50] text-sm">Confirm Password</Label>
                  <Input
                    id="auth-confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-1"
                    data-testid="auth-gate-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 text-lg font-medium transition-all shadow-md"
                  data-testid="auth-gate-create-account-button"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account & Continue'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutAuthGate;
